/* Zeyvorra storefront behaviour: bag drawer, product options, sorting, recommendations */
(function () {
  const routes = window.zvRoutes || {};
  const DRAWER_SECTION = 'zv-cart-drawer';

  /** @returns {HTMLElement | null} */
  const getDrawer = () => document.querySelector('[data-zv-drawer]');

  /** @type {Element | null} */
  let lastFocused = null;

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer || !drawer.hidden) return;

    lastFocused = document.activeElement;
    drawer.hidden = false;
    document.documentElement.classList.add('zv-drawer-open');
    drawer.querySelector('.zv-drawer__panel')?.focus({ preventScroll: true });
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer || drawer.hidden) return;

    drawer.hidden = true;
    document.documentElement.classList.remove('zv-drawer-open');
    if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
  }

  /**
   * Replaces the drawer contents and bag counts from section HTML.
   * @param {string} html
   */
  function renderDrawer(html) {
    const drawer = getDrawer();
    if (!drawer || !html) return;

    const next = new DOMParser().parseFromString(html, 'text/html');
    const nextContent = next.querySelector('[data-zv-drawer-content]');
    const content = drawer.querySelector('[data-zv-drawer-content]');
    if (!nextContent || !content) return;

    content.innerHTML = nextContent.innerHTML;

    const count = nextContent.querySelector('[data-zv-cart-count]')?.textContent ?? '';
    for (const element of document.querySelectorAll('[data-zv-cart-count]')) {
      element.textContent = count;
    }
  }

  async function refreshDrawer() {
    const response = await fetch(`${window.location.pathname}?section_id=${DRAWER_SECTION}`);
    if (response.ok) renderDrawer(await response.text());
  }

  /**
   * @param {string} url
   * @param {Record<string, unknown>} body
   */
  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: DRAWER_SECTION, sections_url: window.location.pathname }),
    });
    const data = await response.json();

    if (!response.ok) throw new Error(data.description || data.message || 'Something went wrong.');

    return data;
  }

  /**
   * @param {HTMLElement} button
   */
  async function changeQuantity(button) {
    const drawer = getDrawer();
    drawer?.classList.add('is-busy');

    try {
      const data = await cartRequest(routes.cartChange, {
        line: Number(button.dataset.line),
        quantity: Math.max(0, Number(button.dataset.quantity)),
      });
      renderDrawer(data.sections?.[DRAWER_SECTION]);
    } catch (error) {
      await refreshDrawer();
    } finally {
      drawer?.classList.remove('is-busy');
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  async function addToBag(form) {
    const button = form.querySelector('[data-zv-add]');
    const errorElement = form.querySelector('[data-zv-error]');
    const formData = new FormData(form);

    if (button instanceof HTMLButtonElement) button.disabled = true;
    if (errorElement instanceof HTMLElement) errorElement.hidden = true;

    try {
      const data = await cartRequest(routes.cartAdd, {
        items: [{ id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) }],
      });
      renderDrawer(data.sections?.[DRAWER_SECTION]);
      openDrawer();
    } catch (error) {
      if (errorElement instanceof HTMLElement) {
        errorElement.textContent = error instanceof Error ? error.message : String(error);
        errorElement.hidden = false;
      }
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }

  /**
   * Syncs price, button, URL and availability with the selected options.
   * @param {HTMLElement} root
   */
  function updateVariant(root) {
    const dataElement = root.querySelector('[data-zv-variants]');
    if (!dataElement) return;

    /** @type {{ id: number, options: string[], available: boolean, price: string, compare: string | null, media: number | null }[]} */
    const variants = JSON.parse(dataElement.textContent || '[]');
    const groups = Array.from(root.querySelectorAll('[data-zv-option]'));
    const selected = groups.map((group) => {
      const input = group.querySelector('input:checked');
      const value = input instanceof HTMLInputElement ? input.value : '';
      const label = group.querySelector('[data-zv-option-value]');
      if (label) label.textContent = value;
      return value;
    });

    const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));

    // Mark values that have no purchasable variant with the rest of the current selection
    groups.forEach((group, groupIndex) => {
      for (const input of group.querySelectorAll('input')) {
        const candidate = selected.map((value, index) => (index === groupIndex ? input.value : value));
        const match = variants.find((item) => item.options.every((option, index) => option === candidate[index]));
        input.classList.toggle('is-unavailable', !match || !match.available);
      }
    });

    const button = root.querySelector('[data-zv-add]');
    const price = root.querySelector('[data-zv-price]');
    const idInput = root.querySelector('[data-zv-variant-id]');

    if (!(button instanceof HTMLButtonElement)) return;

    if (!variant || !variant.available) {
      button.disabled = true;
      button.textContent = button.dataset.soldOutLabel || '';
      if (!variant) return;
    } else {
      button.disabled = false;
      button.textContent = `${button.dataset.label} — ${variant.price}`;
    }

    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);

    if (price) {
      price.textContent = '';
      if (variant.compare) {
        const compare = document.createElement('s');
        compare.textContent = variant.compare;
        price.append(compare, ' ');
      }
      price.append(variant.price);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);

    if (variant.media) {
      const gallery = root.querySelector('.zv-gallery');
      const media = gallery?.querySelector(`[data-media-id="${variant.media}"]`);
      if (gallery && media && media !== gallery.firstElementChild) gallery.prepend(media);
    }
  }

  async function loadRecommendations() {
    for (const section of document.querySelectorAll('[data-zv-recommendations]')) {
      if (!(section instanceof HTMLElement)) continue;

      const { productId, sectionId, limit } = section.dataset;
      const list = section.querySelector('[data-zv-recommendations-list]');
      if (!productId || !list || list.children.length > 0) continue;

      try {
        const url = `${routes.recommendations}?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=related`;
        const response = await fetch(url);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextList = html.querySelector('[data-zv-recommendations-list]');

        if (nextList && nextList.children.length > 0) {
          list.innerHTML = nextList.innerHTML;
        } else {
          section.hidden = true;
        }
      } catch (error) {
        section.hidden = true;
      }
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-zv-cart-open]') && getDrawer()) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (target.closest('[data-zv-cart-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const quantityButton = target.closest('[data-zv-qty]');
    if (quantityButton instanceof HTMLElement) {
      event.preventDefault();
      changeQuantity(quantityButton);
      return;
    }

    // Fall back to the search page when the search dialog is not on the page
    if (target.closest('[data-zv-search]') && !document.querySelector('#search-modal')) {
      window.location.href = routes.search;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('zv-product__form')) return;
    if (!getDrawer()) return;

    event.preventDefault();
    addToBag(form);
  });

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (target instanceof HTMLInputElement && target.closest('[data-zv-option]')) {
      const root = target.closest('[data-zv-product]');
      if (root instanceof HTMLElement) updateVariant(root);
      return;
    }

    if (target instanceof HTMLSelectElement && target.matches('[data-zv-sort]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', target.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });

  // Keep the bag in sync when other theme components change the cart
  document.addEventListener('cart:update', () => {
    refreshDrawer();
  });

  function init() {
    loadRecommendations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
